import { z } from 'zod';
import dayjs from 'dayjs';
import { employmentApi } from '../../../api';
import { EMPLOYMENT_EVENT_TYPES } from '../../../../constants/employmentEventTypes';
import { EVENT_ACTOR_TYPES } from '../../../../constants/employmentActorTypes';
import { DEFAULT_SOURCE_SYSTEM, toSourceContractId } from '../../../../constants/employmentSourceSystems';
import { employmentEpisodeIdSchema, withEpisode } from '../episodeAddress';

/**
 * ADR-136 phase 5 — contract-schedule verbs, composed from the PRODUCTION resign
 * events per §5a (the Americas sync's own correction sequence, promoted to UI
 * verbs). One trigger call = one transaction = no partial states. No backend
 * handler or payload changes beyond the optional processOptions.closeComment,
 * so the radar-ingestion syncs are untouched (§2a.7).
 *
 * The withdraw-first shape is what makes the audit trail survive: the original
 * RESIGNATION_SUBMITTED stays Valid, the original Pending TERMINATION_FINALIZED
 * flips to Cancelled, the withdraw stamps when and why, and the fresh pair
 * carries the new date — strictly better than the in-place effectiveOn
 * overwrite, which the withdraw-first shape bypasses by construction.
 */

const changeContractEndSchema = z.object({
  employeeId: z.uuid(),
  employmentEpisodeId: employmentEpisodeIdSchema, // ADR-155 §6
  newEndDate: z.iso.date('New end date must be in ISO format (YYYY-MM-DD)'),
  // Why the end is changing — stamps the RESIGNATION_WITHDRAWN leg.
  reason: z.string().min(1, 'Reason is required'),
  // The new end's own reason text (for a fixed-term contract, the regenerated
  // A2 sentence) — lands in resignationReason and on the employee card.
  resignationReason: z.string().min(1, 'The new end reason text is required'),
  // ADR-171 C2 / ADR-153 R1 — "end these too" (primary only)
  endOtherContractsToo: z.boolean().optional()
});

/**
 * Change a scheduled contract end — since ADR-143 phase 3, a CONTRACT
 * AMENDMENT (§9: the verb emits the amendment; the three-event dance this
 * function used to compose is retired for end-date changes).
 *
 * One POST to the episode-addressed amendment route: the backend records
 * `employment.contract.amended` with changes/previous termEndDate and — the
 * amendment being effective immediately — moves the episode's Pending
 * TERMINATION_FINALIZED to the new date in the same transaction (§7's one
 * mechanism write; deriveTermEnd reads either representation, they flip
 * together). Works for any new date, including through the post-expiry grace
 * window — the pending termination is still Pending there, which is the only
 * precondition (the backend rejects an end-date amendment when no scheduled
 * end exists to move).
 *
 * The old `resignationReason` input survives as the amendment's
 * `endReasonText` — the regenerated fixed-term sentence the backend writes
 * to the (transitional, ADR-144 §11.2) employee resignReason singleton.
 *
 * The actorType parameter is retained for signature compatibility but no
 * longer transmitted: the amendment route derives the actor from the
 * authenticated principal (§9 reason 3) and machine principals are refused
 * by Gate B.
 */
export const changeContractEnd = async (payload, _actorType = EVENT_ACTOR_TYPES.USER) => {
  const p = changeContractEndSchema.parse(payload);

  // Resolve the contract being amended: the amendment surface is
  // episode-addressed by construction (ADR-143 §9), and the scheduled end
  // being moved lives on the OPEN episode.
  // ADR-155 §6 — the named contract wins; otherwise the person's open one.
  const episodes = await employmentApi.getEmploymentEpisodes(p.employeeId);
  const openEpisode = (episodes || []).find((episode) =>
    p.employmentEpisodeId ? episode.id === p.employmentEpisodeId && !episode.endDate : !episode.endDate);

  if (!openEpisode) {
    throw new Error(p.employmentEpisodeId
      ? 'The named contract is not one of this person\'s open contracts — there is no scheduled end to change'
      : 'This person has no open contract — there is no scheduled end to change');
  }

  return await employmentApi.createContractAmendment(openEpisode.id, {
    changes: { termEndDate: p.newEndDate },
    reason: p.reason,
    endReasonText: p.resignationReason,
    ...(p.endOtherContractsToo ? { endOtherContractsToo: true } : {})
  });
};

const cancelContractSchema = z.object({
  employeeId: z.uuid(),
  employmentEpisodeId: employmentEpisodeIdSchema, // ADR-155 §6
  cancelDate: z.iso.date('Cancellation date must be in ISO format (YYYY-MM-DD)'),
  reason: z.string().min(1, 'Reason is required'),
  // ADR-171 C2 / ADR-153 R1 — "end these too" (primary only)
  endOtherContractsToo: z.boolean().optional()
});

/**
 * Cancel a contract with a date — ONE verb, ADR-171 N5.
 *
 * Sends `employment.contract.cancelled`. The backend replays the legs this
 * function used to compose (B1 pointed at "now"): the scheduled end is
 * withdrawn first when the contract has one; the cancellation is submitted at
 * the date; when that date is today or past the finalize leg is forced
 * (ADR-048), so the contract closes AT the cancellation date, the "cancelled
 * early" disposition is stamped and the end reason is CANCELLED_BEFORE_TERM.
 * A future date is never enacted early: the end stays Pending and the
 * scheduler enacts it after the grace window. One Valid
 * `employment.contract.cancelled` event records who, when and why.
 */
export const cancelContract = async (payload, actorType = EVENT_ACTOR_TYPES.USER) => {
  const p = cancelContractSchema.parse(payload);

  return await employmentApi.triggerEmploymentEvents({
    metadata: { actorType },
    events: [{
      type: EMPLOYMENT_EVENT_TYPES.CONTRACT_CANCELLED,
      payload: {
        employeeId: p.employeeId,
        ...withEpisode(p),
        cancelDate: p.cancelDate,
        reason: p.reason,
        ...(p.endOtherContractsToo ? { endOtherContractsToo: true } : {})
      }
    }]
  });
};

const replaceContractSchema = z.object({
  employeeId: z.uuid(),
  employmentEpisodeId: employmentEpisodeIdSchema, // ADR-155 §6
  // The old contract's end (its LAST DAY) / the successor's anchor. PAST ONLY
  // (ADR-179 D2): a cutover of today or later is still in force and is
  // scheduled instead (scheduleReplacement), switched the morning after.
  cutoverDate: z.iso.date('Cutover date must be in ISO format (YYYY-MM-DD)'),
  reason: z.string().min(1, 'Reason is required'),
  // §2a settlement #7: skipResignEmployee — allocations survive the cutover.
  keepProjects: z.boolean().optional(),
  // True when the current contract has a scheduled end to withdraw first.
  withdrawScheduledEnd: z.boolean().optional(),
  // Successor contract (mirrors the rehire wrapper)
  position: z.uuid(),
  // ADR-139 — nullable, and the null is load-bearing: it says the successor's
  // position requires no grade. Omitted still means "unchanged" (the REHIRE
  // handler's pre-ADR-139 semantics), which is what keeps sync callers untouched.
  grade: z.uuid().nullable().optional(),
  contractType: z.uuid(),
  contractCompany: z.uuid(),
  // ADR-179 §8 — sent so the successor gets the model shown on the form
  engagementModel: z.uuid().optional(),
  workingLocation: z.uuid(),
  cluster: z.uuid(),
  hireDate: z.iso.date('Contract start date must be in ISO format (YYYY-MM-DD)'),
  contractNumber: z.string().optional(),
  employeeHireDate: z.iso.date('Employee hire date must be in ISO format (YYYY-MM-DD)').optional(),
  contractEndDate: z.iso.date('Contract end date must be in ISO format (YYYY-MM-DD)').optional(),
  contractEndReason: z.string().min(1).optional()
}).refine(
  (data) => data.cutoverDate < dayjs().format('YYYY-MM-DD'),
  { message: 'The cutover date must be in the past — for today or later, schedule the replacement', path: ['cutoverDate'] }
).refine(
  (data) => data.hireDate > data.cutoverDate,
  { message: 'The new contract must start after the cutover date', path: ['hireDate'] }
).refine(
  (data) => !data.contractEndDate || data.contractEndDate > data.hireDate,
  { message: 'Contract end date must be after the contract start date', path: ['contractEndDate'] }
).refine(
  (data) => !data.contractEndDate || Boolean(data.contractEndReason && data.contractEndReason.trim()),
  { message: 'A fixed-term comment is required when a contract end date is set', path: ['contractEndReason'] }
);

/**
 * Replace a running contract with a successor — ONE verb, ADR-171 N1.
 *
 * Sends `employment.contract.replaced`. The backend composes the §5a legs
 * (withdraw-first when the contract has an active scheduled end, the forced
 * cancel at the cutover with the "renewed" disposition, the successor and its
 * own fixed-term pair) and replays them in one transaction — the same legs
 * this function used to compose, now in one place shared with the
 * scheduled-replacement sweep. keepProjects (§2a settlement #7) still maps to
 * skipResignEmployee there. `withdrawScheduledEnd` is no longer sent: the
 * backend decides it from the contract's state when the verb runs.
 *
 * What the single verb adds (ADR-171 N1): the successor takes the primary
 * and the person facts only when the replaced contract WAS the primary, and
 * its terms carry forward from the replaced contract.
 */
export const replaceContract = async (payload, actorType = EVENT_ACTOR_TYPES.USER) => {
  const p = replaceContractSchema.parse(payload);

  const contractId = toSourceContractId(p.contractNumber);
  // ADR-160 §5.4 — the stamp is unconditional: a radar contract without a
  // number used to carry no sourceReference at all, which radar-api reads as
  // radar-owned but the Americas sync reads as ADOPTABLE by start date. The
  // contract number joins the stamp when there is one.
  const sourceReference = contractId === null
    ? { source: DEFAULT_SOURCE_SYSTEM }
    : { source: DEFAULT_SOURCE_SYSTEM, contractId };

  const fixedTermReason = p.contractEndDate ? p.contractEndReason.trim() : null;

  return await employmentApi.triggerEmploymentEvents({
    metadata: { actorType },
    events: [{
      type: EMPLOYMENT_EVENT_TYPES.CONTRACT_REPLACED,
      payload: {
        employeeId: p.employeeId,
        ...withEpisode(p), // the contract being REPLACED (ADR-155 §5)
        cutoverDate: p.cutoverDate,
        reason: p.reason,
        keepProjects: Boolean(p.keepProjects),
        positionId: p.position,
        // ADR-139 — transmit an explicit null, omit only a genuinely absent key.
        ...(p.grade !== undefined ? { gradeId: p.grade } : {}),
        contractTypeId: p.contractType,
        contractCompanyId: p.contractCompany,
        ...(p.engagementModel ? { engagementModelId: p.engagementModel } : {}),
        workingLocationId: p.workingLocation,
        clusterId: p.cluster,
        contractStartDate: p.hireDate,
        ...(p.employeeHireDate ? { hireDate: p.employeeHireDate } : {}),
        sourceReference,
        ...(fixedTermReason ? {
          comments: fixedTermReason,
          contractEndDate: p.contractEndDate,
          contractEndReason: fixedTermReason
        } : {})
      }
    }]
  });
};

const scheduleReplacementSchema = z.object({
  employeeId: z.uuid(),
  employmentEpisodeId: employmentEpisodeIdSchema, // ADR-155 §6
  // TODAY OR LATER (ADR-179 D2) — only a past cutover is the immediate
  // replaceContract verb.
  cutoverDate: z.iso.date('Cutover date must be in ISO format (YYYY-MM-DD)'),
  reason: z.string().min(1, 'Reason is required'),
  keepProjects: z.boolean().optional(),
  withdrawScheduledEnd: z.boolean().optional(),
  position: z.uuid(),
  // ADR-139 — see replaceContractSchema. The null must survive storage in
  // employment_events.payload, because the phase-6 sweep replays it verbatim.
  grade: z.uuid().nullable().optional(),
  contractType: z.uuid(),
  contractCompany: z.uuid(),
  // ADR-179 §8 — sent so the successor gets the model shown on the form
  engagementModel: z.uuid().optional(),
  workingLocation: z.uuid(),
  cluster: z.uuid(),
  hireDate: z.iso.date('Contract start date must be in ISO format (YYYY-MM-DD)'),
  contractNumber: z.string().optional(),
  employeeHireDate: z.iso.date('Employee hire date must be in ISO format (YYYY-MM-DD)').optional(),
  contractEndDate: z.iso.date('Contract end date must be in ISO format (YYYY-MM-DD)').optional(),
  contractEndReason: z.string().min(1).optional()
}).refine(
  (data) => data.cutoverDate >= dayjs().format('YYYY-MM-DD'),
  { message: 'The cutover date must be today or later — for a past cutover use Replace Contract directly', path: ['cutoverDate'] }
).refine(
  (data) => data.hireDate > data.cutoverDate,
  { message: 'The new contract must start after the cutover date', path: ['hireDate'] }
).refine(
  (data) => !data.contractEndDate || data.contractEndDate > data.hireDate,
  { message: 'Contract end date must be after the contract start date', path: ['contractEndDate'] }
).refine(
  (data) => !data.contractEndDate || Boolean(data.contractEndReason && data.contractEndReason.trim()),
  { message: 'A fixed-term comment is required when a contract end date is set', path: ['contractEndReason'] }
);

/**
 * ADR-136 phase 6 (§15) — schedule a future-dated contract replacement/renewal.
 *
 * Emits ONE event: RENEWAL_SCHEDULED, recorded Pending on the current episode
 * with effectiveOn = the cutover date and the full replacement payload persisted
 * in employment_events.payload. Nothing about the person changes until the
 * process-pending-renewals sweep replays the payload through the production
 * handlers on the cutover date; cancelling before then flips the event to
 * Cancelled (the B2 pattern) and restores nothing because nothing was touched.
 * The payload is stored HANDLER-shaped (positionId, contractStartDate, …) so
 * the sweep replays it without any mapping to drift.
 */
export const scheduleReplacement = async (payload, actorType = EVENT_ACTOR_TYPES.USER) => {
  const p = scheduleReplacementSchema.parse(payload);

  const contractId = toSourceContractId(p.contractNumber);
  // ADR-160 §5.4 — the stamp is unconditional: a radar contract without a
  // number used to carry no sourceReference at all, which radar-api reads as
  // radar-owned but the Americas sync reads as ADOPTABLE by start date. The
  // contract number joins the stamp when there is one.
  const sourceReference = contractId === null
    ? { source: DEFAULT_SOURCE_SYSTEM }
    : { source: DEFAULT_SOURCE_SYSTEM, contractId };

  const fixedTermReason = p.contractEndDate ? p.contractEndReason.trim() : null;

  return await employmentApi.triggerEmploymentEvents({
    metadata: { actorType },
    events: [{
      type: EMPLOYMENT_EVENT_TYPES.RENEWAL_SCHEDULED,
      payload: {
        employeeId: p.employeeId,
        ...withEpisode(p),
        cutoverDate: p.cutoverDate,
        reason: p.reason,
        ...(p.keepProjects !== undefined ? { keepProjects: p.keepProjects } : {}),
        ...(p.withdrawScheduledEnd !== undefined ? { withdrawScheduledEnd: p.withdrawScheduledEnd } : {}),
        positionId: p.position,
        // ADR-139 — `!== undefined`, not truthiness: a null says the successor's
        // position has no grade and must reach the sweep.
        ...(p.grade !== undefined ? { gradeId: p.grade } : {}),
        contractTypeId: p.contractType,
        contractCompanyId: p.contractCompany,
        ...(p.engagementModel ? { engagementModelId: p.engagementModel } : {}),
        workingLocationId: p.workingLocation,
        clusterId: p.cluster,
        contractStartDate: p.hireDate,
        ...(p.employeeHireDate ? { hireDate: p.employeeHireDate } : {}),
        ...(sourceReference ? { sourceReference } : {}),
        ...(fixedTermReason ? { comments: fixedTermReason } : {}),
        ...(p.contractEndDate ? {
          contractEndDate: p.contractEndDate,
          contractEndReason: fixedTermReason
        } : {})
      }
    }]
  });
};

const cancelScheduledRenewalSchema = z.object({
  employeeId: z.uuid(),
  employmentEpisodeId: employmentEpisodeIdSchema, // ADR-155 §6
  // ADR-155 §4 (ADR-144 §6.4) — the intent to cancel, named. The Contracts tab
  // sends it always (the renewal row holds the event); absent, the server
  // resolves it only when the person has exactly one pending renewal.
  renewalEventId: z.uuid().optional(),
  reason: z.string().min(1, 'Reason is required')
});

/**
 * ADR-136 phase 6 (§15 slice 3) — cancel a pending scheduled replacement.
 *
 * The B2 pattern: ONE event, RENEWAL_CANCELLED, recorded Valid; server-side
 * the pending RENEWAL_SCHEDULED flips to Cancelled in the same transaction.
 * Nothing else changes — scheduling changed nothing, so cancelling restores
 * state by doing nothing. Valid right up to the cutover day (the sweep's
 * state-guarded claim means whichever lands first wins cleanly).
 */
export const cancelScheduledRenewal = async (payload, actorType = EVENT_ACTOR_TYPES.USER) => {
  const p = cancelScheduledRenewalSchema.parse(payload);

  return await employmentApi.triggerEmploymentEvents({
    metadata: { actorType },
    events: [{
      type: EMPLOYMENT_EVENT_TYPES.RENEWAL_CANCELLED,
      payload: {
        employeeId: p.employeeId,
        ...withEpisode(p),
        ...(p.renewalEventId ? { renewalEventId: p.renewalEventId } : {}),
        reason: p.reason
      }
    }]
  });
};
